import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, Clock, ArrowRight, Search, Tag, TrendingUp } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { API } from '../App';

// Dynamically generate categories from active blogs or maintain standard list
const DEFAULT_CATEGORIES = ["All", "news", "tips", "guides", "technology", "finance"];

const BlogPage = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const res = await axios.get(`${API}/api/blogs`);
                // Filter to only published blogs
                const publishedBlogs = res.data.filter(b => b.is_published);
                setBlogs(publishedBlogs);
            } catch (error) {
                console.error("Failed to fetch blogs:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    // Extract dynamic categories from blogs, append to 'All'
    const categories = ["All", ...new Set(blogs.map(b => b.category))];

    // Filter posts
    const filteredPosts = blogs.filter(post => {
        const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const featuredPosts = blogs.filter(post => post.featured || post.views > 100).slice(0, 2); // Show top 2 as featured or high views

    return (
        <div className="min-h-screen md:pt-20">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-blue-900 via-cyan-800 to-blue-900 py-16 md:py-24 -mt-14 pt-14 md:mt-0 md:pt-0">
                <div className="container-solar text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                            Solar Energy <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-300">Blog</span>
                        </h1>
                        <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8">
                            Stay informed with the latest solar news, tips, and insights from industry experts
                        </p>

                        {/* Search Bar */}
                        <div className="max-w-xl mx-auto relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search articles..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-12 py-6 text-lg rounded-full bg-white/10 backdrop-blur-md border-white/20 text-white placeholder:text-white/50"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Categories */}
            <section className="py-8 bg-muted/30 border-b">
                <div className="container-solar">
                    <div className="flex flex-wrap gap-2 justify-center">
                        {categories.map((category) => (
                            <Button
                                key={category}
                                variant={selectedCategory === category ? "default" : "outline"}
                                size="sm"
                                onClick={() => setSelectedCategory(category)}
                                className={selectedCategory === category ? "btn-primary" : ""}
                            >
                                {category}
                            </Button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Posts */}
            {selectedCategory === 'All' && searchQuery === '' && (
                <section className="py-16">
                    <div className="container-solar">
                        <div className="flex items-center gap-2 mb-8">
                            <TrendingUp className="w-6 h-6 text-primary" />
                            <h2 className="text-2xl font-bold">Featured Articles</h2>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            {featuredPosts.map((post, index) => (
                                <motion.article
                                    key={post.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="group bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border"
                                >
                                    <div className="relative h-64 overflow-hidden">
                                        <img
                                            src={post.image}
                                            alt={post.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                        <Badge className="absolute top-4 left-4 bg-primary text-white">
                                            {post.category}
                                        </Badge>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                {new Date(post.created_at).toLocaleDateString()}
                                            </span>
                                            <span className="flex items-center gap-1 capitalize">
                                                <Tag className="w-4 h-4" />
                                                {post.category}
                                            </span>
                                        </div>
                                        <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                                            {post.title}
                                        </h3>
                                        <p className="text-muted-foreground mb-4 line-clamp-2">
                                            {post.excerpt}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <span className="flex items-center gap-2 text-sm">
                                                <User className="w-4 h-4" />
                                                {post.author_name}
                                            </span>
                                            <Link to={`/blog/${post.id}`}>
                                                <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80">
                                                    Read More <ArrowRight className="w-4 h-4 ml-1" />
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* All Posts Grid */}
            <section className="py-16 bg-muted/30">
                <div className="container-solar">
                    <h2 className="text-2xl font-bold mb-8">
                        {selectedCategory === 'All' ? 'Latest Articles' : `${selectedCategory} Articles`}
                    </h2>

                    {filteredPosts.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-muted-foreground text-lg">No articles found matching your criteria.</p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
                            >
                                Reset Filters
                            </Button>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredPosts.map((post, index) => (
                                <motion.article
                                    key={post.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="group bg-card rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 border"
                                >
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src={post.image_url || "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800"}
                                            alt={post.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <Badge className="absolute top-3 left-3 bg-primary/90 text-white text-xs">
                                            {post.category}
                                        </Badge>
                                    </div>
                                    <div className="p-5">
                                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(post.created_at).toLocaleDateString()}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                5 min read
                                            </span>
                                        </div>
                                        <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                                            {post.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                            {post.excerpt}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <span className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <User className="w-3 h-3" />
                                                {post.author_name}
                                            </span>
                                            <Link to={`/blog/${post.id}`}>
                                                <Button variant="link" size="sm" className="text-primary p-0">
                                                    Read <ArrowRight className="w-3 h-3 ml-1" />
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Newsletter CTA */}
            <section className="py-16">
                <div className="container-solar">
                    <div className="solar-gradient rounded-3xl p-8 md:p-12 text-center text-white">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            Subscribe to Our Newsletter
                        </h2>
                        <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
                            Get weekly solar tips, industry news, and exclusive offers delivered to your inbox
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                            <Input
                                type="email"
                                placeholder="Enter your email"
                                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 flex-1"
                            />
                            <Button className="bg-white text-primary hover:bg-white/90 font-semibold">
                                Subscribe
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BlogPage;
